/**
 * NavSidebar — Collapsible left navigation panel.
 *
 * Organized by case lifecycle stages. Collapses to a
 * slim icon rail with tooltips; expands to show labels.
 * Badges indicate items needing attention.
 *
 * Props:
 *   expanded: boolean — whether sidebar is expanded
 *   onToggle: () => void — toggle expanded state
 *   items: NavigationConfig — structured nav items (optional override)
 */

import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import UserProfileContext from '../../../context/userprofilecontext';
import AIAssistantContext from '../../../context/aiassistantcontext';
import SelectCurrentCustomerDialog from '../../../components/ui/dialog-select-current-customer';
import SelectCurrentCaseDialog from '../../../components/ui/dialog-select-current-case';
import signOutWithCleanup from '../../../utils/signOutWithCleanup';
import styles from './NavSidebar.module.css';

/* ---- Icon helper ---- */
function NavIcon({ name, className }) {
  // Uses Lucide icons via CSS class or inline SVG
  // For now, renders MUI-compatible icon placeholders
  return (
    <span className={`${styles.icon} ${className || ''}`} data-icon={name}>
      {/* Icon rendered via CSS or icon library */}
    </span>
  );
}

/* ---- Single nav item ---- */
function NavItemRow({ item, active, expanded, onClick }) {
  return (
    <button
      className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
      onClick={onClick}
      title={!expanded ? item.displayName : undefined}
      data-tooltip={item.displayName}
    >
      <span className={styles.navItemIcon}>
        {item.icon && <item.icon style={{ fontSize: 20 }} />}
      </span>
      <span className={styles.navItemLabel}>
        {item.displayName}
      </span>
      {item.badge && (
        <>
          <span className={styles.navItemBadge}>{item.badge}</span>
          <span className={styles.navItemDot} />
        </>
      )}
    </button>
  );
}

/* ---- Section header ---- */
function NavSection({ label, children, expanded }) {
  return (
    <div className={styles.section}>
      {label && (
        <div className={styles.sectionLabel}>{label}</div>
      )}
      {children}
    </div>
  );
}

/* ---- Context selector (Customer/Case) ---- */
function ContextSelector({ expanded }) {
  const { userProfile } = useContext(UserProfileContext);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);

  const customer = userProfile?.currentCustomer;
  const customerName = customer?.orgName
    || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
    || null;
  const caseName = userProfile?.currentCase?.caseName;

  return (
    <div className={styles.context}>
      {/* Customer row */}
      <button
        className={styles.contextBtn}
        onClick={() => setCustomerDialogOpen(true)}
        title={!expanded ? `${customerName || 'Select Customer'} / ${caseName || 'Select Case'}` : undefined}
      >
        <span className={styles.contextIcon}>
          {/* Building icon for customer */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/>
            <path d="M9 22v-4h6v4"/>
            <path d="M8 6h.01"/><path d="M16 6h.01"/>
            <path d="M8 10h.01"/><path d="M16 10h.01"/>
            <path d="M8 14h.01"/><path d="M16 14h.01"/>
          </svg>
        </span>
        <span className={styles.contextDetails}>
          <span className={styles.contextLabel}>Customer</span>
          <span className={styles.contextValue}>
            {customerName || 'Select Customer'}
          </span>
        </span>
      </button>

      {/* Case row */}
      <button
        className={styles.contextBtn}
        onClick={() => setCaseDialogOpen(true)}
        title={!expanded ? (caseName || 'Select Case') : undefined}
        disabled={!customerName}
      >
        <span className={styles.contextIcon}>
          {/* Briefcase icon for case */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
        </span>
        <span className={styles.contextDetails}>
          <span className={styles.contextLabel}>Case</span>
          <span className={styles.contextValue}>
            {caseName || 'Select Case'}
          </span>
        </span>
      </button>

      {/* Dialogs */}
      <SelectCurrentCustomerDialog
        open={customerDialogOpen}
        onSubmit={() => setCustomerDialogOpen(false)}
        onCancel={() => setCustomerDialogOpen(false)}
      />
      <SelectCurrentCaseDialog
        open={caseDialogOpen}
        onSubmit={() => setCaseDialogOpen(false)}
        onCancel={() => setCaseDialogOpen(false)}
      />
    </div>
  );
}

/* ---- User profile area ---- */
function NavUserArea({ expanded }) {
  const { userProfile } = useContext(UserProfileContext);
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const user = userProfile?.currentUser;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName
    || user?.email?.split('@')[0]
    || 'User';

  const initials = (fullName
    ? fullName.split(' ').map(n => n[0]).join('')
    : displayName.slice(0, 2)
  ).toUpperCase().slice(0, 2);

  let role = 'Member';
  if (userProfile?.isSuperAdmin) role = 'Super Admin';
  else if (userProfile?.isOrgAdmin?.()) role = 'Org Admin';
  else if (userProfile?.isCaseAdmin?.()) role = 'Case Admin';
  else if (userProfile?.isCaseEditor?.()) role = 'Editor';
  else if (userProfile?.isCaseContributor?.()) role = 'Contributor';
  else if (userProfile?.isCaseViewer?.()) role = 'Viewer';

  const handleSignOut = () => {
    setMenuOpen(false);
    // Clear SWR cache before signOut — see signOutWithCleanup.js header
    // for the cross-user UI leak this closes.
    signOutWithCleanup(signOut);
  };

  const handleMyAccount = () => {
    setMenuOpen(false);
    navigate('/account');
  };

  return (
    <div className={styles.footer} ref={wrapRef}>
      <button
        className={styles.userBtn}
        onClick={() => setMenuOpen((v) => !v)}
        title={!expanded ? `${displayName} — ${role}` : undefined}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className={styles.userAvatar}>{initials}</span>
        <span className={styles.userInfo}>
          <span className={styles.userName}>{displayName}</span>
          <span className={styles.userRole}>{role}</span>
        </span>
      </button>
      {menuOpen && (
        <div className={styles.userMenu} role="menu">
          <button className={styles.userMenuItem} role="menuitem" onClick={handleMyAccount}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span>My account</span>
          </button>
          <button className={styles.userMenuItem} role="menuitem" onClick={handleSignOut}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" x2="9" y1="12" y2="12"/>
            </svg>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ---- Main NavSidebar component ---- */
function NavSidebar({ expanded, onToggle, items }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCenterPanelOverride } = useContext(AIAssistantContext);

  // Every nav-item click clears any agent-rendered center-panel override
  // before navigating. This handles the case where the user clicks the
  // already-active nav (no router event fires, so AppShell's pathname
  // effect doesn't run) and expects to "go back" to the route's default
  // content — e.g., they're on Dashboard, click a topic (TopicDetailPanel
  // overlays), then click Dashboard again wanting the dashboard back.
  // Different-route clicks also clear (defense — the route-change effect
  // already fires, calling this twice is a no-op).
  const handleNavigate = (path) => () => {
    clearCenterPanelOverride();
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  // Default navigation structure grouped by lifecycle stage
  const sections = items || [];

  return (
    <nav className={`${styles.sidebar} ${expanded ? styles.expanded : ''}`}>
      {/* Brand */}
      <div className={styles.brand} onClick={onToggle}>
        <div className={styles.brandIcon}>L</div>
        <div className={styles.brandText}>Livisibly</div>
      </div>

      {/* Context selector */}
      <ContextSelector expanded={expanded} />

      {/* Scrollable nav items */}
      <div className={styles.scroll}>
        {sections.map((section, sIdx) => (
          <NavSection key={sIdx} label={section.label} expanded={expanded}>
            {section.items
              .filter(item => !item.hidden)
              .map((item, iIdx) => (
                <NavItemRow
                  key={iIdx}
                  item={item}
                  active={isActive(item.path)}
                  expanded={expanded}
                  onClick={handleNavigate(item.path)}
                />
              ))}
          </NavSection>
        ))}
      </div>

      {/* User area */}
      <NavUserArea expanded={expanded} />

      {/* Collapse toggle */}
      <button
        className={styles.collapseToggle}
        onClick={onToggle}
        aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transform: expanded ? 'none' : 'rotate(180deg)', transition: 'transform var(--transition-base)' }}
        >
          <path d="m15 18-6-6 6-6"/>
        </svg>
      </button>
    </nav>
  );
}

export default NavSidebar;
